import React, { useCallback, useEffect, useState } from "react";
import { Layers } from "lucide-react";
import { listTables, getFinding } from "../../lib/uploadsApi";
import { useRole } from "../../lib/role.jsx";
import { Side, SampleGrid } from "../uploads/FileUploadSource";
import TableDrillDrawer from "../datatables/TableDrillDrawer";
import CuratedCard from "./CuratedCard";
import { num } from "../pipeline/Layer";

export function useCurated(onToast) {
  const [tables, setTables] = useState(null);
  const load = useCallback(() => listTables().then(r => setTables(r.tables)).catch(e => { setTables([]); onToast(e.message); }), [onToast]);
  useEffect(() => { load(); }, [load]);
  return [tables, load];
}

export function FindingPanel({ finding, onClose }) {
  return (
    <Side onClose={onClose} testid="ct-finding-panel" wide>
      <div className="p-5 space-y-3"><div className="font-display text-lg font-semibold pr-10">{finding.finding.title}</div>
        <div className="text-[12px] text-muted">{finding.error || `Showing ${finding.rows.length} of ${num(finding.finding.count)} affected rows`}</div><SampleGrid rows={finding.rows} /></div>
    </Side>
  );
}

// Curated tables for one stage, with findings and row drill-downs.
export function CuratedList({ tables, stage, onChanged, onToast, empty }) {
  const { role } = useRole();
  const canEdit = ["it_head", "coo"].includes(role?.id);
  const [finding, setFinding] = useState(null);
  const [rows, setRows] = useState(null);
  const shown = (tables || []).filter(t => (stage === "gold" ? t.stage === "gold" : t.stage !== "gold"));
  const openFinding = (t, f) => getFinding(t, f).then(setFinding).catch(e => setFinding({ finding: { title: "Couldn't load these rows", count: 0 }, rows: [], error: e.message }));
  return (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {shown.map(t => <CuratedCard key={t.type} t={t} canEdit={canEdit} onFinding={openFinding} onRows={(x) => setRows(`up.${x}`)} onChanged={onChanged} onToast={onToast} />)}
      </div>
      {tables && !shown.length && <div className="tile p-8 text-center text-[12.5px] text-muted" data-testid={`ct-empty-${stage}`}>{empty}</div>}
      {!tables && <div className="tile p-6 text-[12px] text-muted">Loading tables…</div>}
      {finding && <FindingPanel finding={finding} onClose={() => setFinding(null)} />}
      {rows && <TableDrillDrawer name={rows} onClose={() => setRows(null)} />}
    </>
  );
}

// Console → Silver: curated tables promoted through Bronze review, ready to be certified Gold.
export default function SilverTab({ onToast, onChanged }) {
  const [tables, load] = useCurated(onToast);
  const changed = async () => { await load(); onChanged(); };
  return (
    <div className="space-y-4" data-testid="tab-silver">
      <div className="text-[12.5px] text-muted inline-flex items-center gap-1.5"><Layers size={13} className="text-primary" />
        Silver holds the cleaned rows of every Bronze item promoted through review. Gold needs a quality score of at least 80 and no blocking finding.</div>
      <CuratedList tables={tables} stage="silver" onChanged={changed} onToast={onToast}
                   empty="No Silver tables yet. Promote a reviewed Bronze item to Silver in the Bronze tab." />
    </div>
  );
}
